import { Component, Input, OnInit } from '@angular/core';
import {
  CdkDrag,
  CdkDragDrop,
  CdkDropList,
  DragDropModule,
  moveItemInArray,
} from '@angular/cdk/drag-drop';
import { NzTableModule } from 'ng-zorro-antd/table';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { SvgIconComponent } from 'angular-svg-icon';
import { NzFormItemComponent } from 'ng-zorro-antd/form';
import { NzInputModule } from 'ng-zorro-antd/input';
import {
  FormArray,
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { CommonModule } from '@angular/common';
import { NzMessageService } from 'ng-zorro-antd/message';

interface DetailPromptsList {
  key: string;
  sequence: string;
  detailPrompt: string;
  action: string;
}

@Component({
  selector: 'app-detail-prompts',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    NzFormItemComponent,
    NzTableModule,
    NzButtonModule,
    SvgIconComponent,
    NzInputModule,
    CdkDropList,
    CdkDrag,
    DragDropModule,
  ],
  templateUrl: './detail-prompts.component.html',
  styleUrl: './detail-prompts.component.scss',
})
export class DetailPromptsComponent implements OnInit {
  @Input() promptsForm!: FormGroup;

  listOfData: DetailPromptsList[] = [];

  readonly maxLength = 500;
  readonly minLength = 3;

  constructor(
    private fb: FormBuilder,
    private message: NzMessageService
  ) {}

  ngOnInit() {
    this.initializeFormArray();
  }

  private initializeFormArray() {
    if (!this.promptsForm.get('items')) {
      this.promptsForm.addControl('items', this.fb.array([]));
    }

    if (this.getItemsFormArray().length === 0) {
      this.initializeDefaultData();
    } else {
      this.updateListFromForm();
    }
  }

  private getItemsFormArray(): FormArray {
    return this.promptsForm.get('items') as FormArray;
  }

  private initializeDefaultData() {
    if (this.listOfData.length === 0) {
      this.listOfData = [
        {
          key: '1',
          sequence: '1',
          detailPrompt: '',
          action: '',
        },
      ];
    }
    this.updateFormFromList();
  }

  private updateListFromForm() {
    const itemsArray = this.getItemsFormArray();
    this.listOfData = itemsArray.controls.map((control, index) => ({
      key: (index + 1).toString(),
      sequence: (index + 1).toString(),
      detailPrompt: (control as FormGroup).get('detailPrompt')?.value || '',
      action: (control as FormGroup).get('action')?.value || '',
    }));
  }

  private updateFormFromList() {
    const itemsArray = this.getItemsFormArray();
    itemsArray.clear();

    this.listOfData.forEach(item => {
      itemsArray.push(
        this.fb.group({
          key: [item.key],
          sequence: [item.sequence],
          detailPrompt: [
            item.detailPrompt,
            [
              Validators.required,
              Validators.minLength(this.minLength),
              Validators.maxLength(this.maxLength),
            ],
          ],
          action: [item.action],
        })
      );
    });
  }

  addRow() {
    const lastItemIndex = this.listOfData.length - 1;
    if (
      lastItemIndex >= 0 &&
      !this.listOfData[lastItemIndex].detailPrompt.trim()
    ) {
      this.message.warning(
        'Please fill in the current prompt before adding a new one'
      );
      return;
    }

    const newItem: DetailPromptsList = {
      key: (this.listOfData.length + 1).toString(),
      sequence: (this.listOfData.length + 1).toString(),
      detailPrompt: '',
      action: '',
    };

    this.listOfData.push(newItem);
    this.updateFormFromList();

    const index = this.listOfData.length - 1;
    const control = this.getItemsFormArray().at(index)?.get('detailPrompt');
    control?.markAsTouched();
  }

  canAddNewRow(): boolean {
    const lastItemIndex = this.listOfData.length - 1;
    return (
      lastItemIndex < 0 || !!this.listOfData[lastItemIndex].detailPrompt.trim()
    );
  }

  removeRow(index: number) {
    this.listOfData.splice(index, 1);
    this.listOfData.forEach((item, idx) => {
      item.sequence = (idx + 1).toString();
      item.key = (idx + 1).toString();
    });
    this.updateFormFromList();
  }

  updateDetailPrompt(index: number, event: Event) {
    const target = event.target as HTMLTextAreaElement;
    this.listOfData[index].detailPrompt = target.value;
    this.updateFormFromList();

    const control = this.getItemsFormArray().at(index)?.get('detailPrompt');
    control?.markAsTouched();
  }

  drop(event: CdkDragDrop<DetailPromptsList[]>) {
    moveItemInArray(this.listOfData, event.previousIndex, event.currentIndex);
    this.listOfData.forEach((item, index) => {
      item.sequence = (index + 1).toString();
      item.key = (index + 1).toString();
    });
    this.updateFormFromList();
  }

  isInvalid(index: number): boolean {
    const control = this.getItemsFormArray().at(index)?.get('detailPrompt');
    return !!control?.invalid && (!!control?.dirty || !!control?.touched);
  }

  getError(index: number): string {
    const control = this.getItemsFormArray().at(index)?.get('detailPrompt');
    if (!control?.errors || (!control.dirty && !control.touched)) return '';

    const messages = {
      required: 'Detail prompt is required.',
      minlength: `Minimum ${this.minLength} characters required.`,
      maxlength: `Maximum ${this.maxLength} characters allowed.`,
    };

    const errorType = Object.keys(control.errors)[0] as keyof typeof messages;
    return messages[errorType] || 'Invalid input.';
  }
}
